import { type ReactNode } from 'react'
import { cn } from '@/shared/lib/utils'
import { SideNav } from '@/shared/ui/side-nav'
import { useKeyboardVisible } from '@/shared/lib/use-keyboard-visible'

export interface AppShellProps {
  /**
   * Содержимое приложения
   */
  children: ReactNode
  /**
   * Показывать ли нижнюю навигацию (на mobile)
   */
  showBottomNav?: boolean
  /**
   * Показывать ли боковую навигацию (на desktop)
   * @default true когда showBottomNav = true
   */
  showSideNav?: boolean
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * UI компонент: Оболочка приложения
 * 
 * Адаптивный layout с поддержкой:
 * - Mobile: нижняя навигация (BottomNav)
 * - Desktop: боковая навигация (SideNav)
 * - Safe area insets для iPhone
 * 
 * @example
 * ```tsx
 * <AppShell showBottomNav>
 *   <main>{children}</main>
 *   <BottomNav />
 * </AppShell>
 * ```
 */
export function AppShell({
  children,
  showBottomNav = false,
  showSideNav,
  className,
}: AppShellProps) {
  // По умолчанию показываем SideNav когда есть BottomNav
  const shouldShowSideNav = showSideNav ?? showBottomNav
  const keyboardVisible = useKeyboardVisible()

  return (
    <div className="app-layout">
      {/* Боковая навигация - только на desktop */}
      {shouldShowSideNav && <SideNav />}

      {/* Основной контент */}
      <div
        className={cn(
          'h-full bg-paper dark:bg-[#0D0F11] text-ink dark:text-white',
          'flex min-h-0 flex-col w-full overflow-hidden',
          shouldShowSideNav && 'main-content',
          // На мобильном добавляем отступ под BottomNav (CSS класс скрывает его на md+)
          showBottomNav && !keyboardVisible && 'bottom-nav-spacer',
          className
        )}
        style={{
          paddingTop: 'var(--top-inset, 0px)',
          paddingBottom: (!showBottomNav || keyboardVisible)
            ? 'env(safe-area-inset-bottom, 0px)'
            : undefined,
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
      >
        {children}
      </div>
    </div>
  )
}
